<script setup>
import { ref, nextTick, watch } from "vue";

/**
 * Renders raw email HTML in an isolated <iframe srcdoc> so the preview matches
 * what an email client (Gmail, Outlook, Apple Mail) actually shows the
 * recipient.
 *
 * Why this exists: the read-only Froala view (<froalaView> / AppFroala
 * :editable="false") injects the email body straight into the app DOM inside a
 * `.fr-view` container. Froala's editor stylesheet then applies
 * `.fr-view table td, .fr-view table th { border: 1px solid #DDD }` to every
 * classed layout cell (see node_modules/froala-editor/css/froala_style.css),
 * and Tailwind's global Preflight reset leaks in too. Email builders (Beefree /
 * Stripo / etc.) lay everything out with nested classed <table> cells, so the
 * preview ends up covered in grey grid lines that the recipient never sees.
 * An iframe gives the email its own document, isolated from app + editor CSS.
 *
 * The iframe is sandboxed WITHOUT `allow-scripts` (email HTML must never run JS
 * in our origin); `allow-same-origin` is kept only so we can read the rendered
 * height to size the frame.
 */
const props = defineProps({
  html: {
    type: String,
    default: "",
  },
  minHeight: {
    type: Number,
    default: 160,
  },
});

const frame = ref(null);

function resize() {
  const el = frame.value;
  if (!el) return;
  try {
    const doc = el.contentDocument;
    if (!doc || !doc.documentElement) return;
    const height = Math.max(
      doc.documentElement.scrollHeight,
      doc.body ? doc.body.scrollHeight : 0,
      props.minHeight
    );
    el.style.height = `${height}px`;
  } catch (e) {
    // contentDocument can be unreadable in edge cases; fall back gracefully.
    el.style.height = `${props.minHeight}px`;
  }
}

function onLoad() {
  nextTick(resize);
  // Safety net: images can finish loading just after the document load event
  // and change the rendered height.
  setTimeout(resize, 250);
}

// Re-measure when the body changes (e.g. switching templates in the wizard).
watch(() => props.html, () => nextTick(resize));
</script>

<template>
  <iframe
    ref="frame"
    :srcdoc="html || ''"
    sandbox="allow-same-origin"
    scrolling="no"
    title="Email preview"
    class="block w-full rounded-lg border border-gray-200 bg-white"
    :style="{ minHeight: `${minHeight}px` }"
    @load="onLoad"
  />
</template>
